import { Link, useLocation, useNavigate } from "react-router-dom";
import { useEffect, useState } from "react";
import { useAuth } from "../contexts/AuthContext";
import { useLang } from "../contexts/LangContext";
import { useCurrency } from "../contexts/CurrencyContext";
import api from "../lib/api";
import { Button } from "../components/ui/button";
import { Coins, Wallet, LayoutDashboard, ShieldCheck, LogOut, Languages, Sparkles, Bell, Gift, Gamepad2, User as UserIcon, DollarSign, Megaphone, Menu, X } from "lucide-react";

export default function Navbar() {
  const { user, logout } = useAuth();
  const { t, lang, toggle } = useLang();
  const { currency, toggle: toggleCurrency } = useCurrency();
  const navigate = useNavigate();
  const location = useLocation();
  const [unread, setUnread] = useState(0);
  const [drawerOpen, setDrawerOpen] = useState(false);

  useEffect(() => {
    if (!user) return;
    const fetch = () => api.get("/notifications").then((r) => setUnread(r.data.unread)).catch(() => {});
    fetch();
    const i = setInterval(fetch, 30000);
    return () => clearInterval(i);
  }, [user, location.pathname]);

  useEffect(() => { setDrawerOpen(false); }, [location.pathname]);

  const handleLogout = async () => { await logout(); navigate("/"); };

  const navItems = user ? [
    { to: "/dashboard", label: t("dashboard"), icon: LayoutDashboard },
    { to: "/pools", label: t("pools"), icon: Sparkles },
    { to: "/wallet", label: t("wallet"), icon: Wallet },
    { to: "/earn", label: t("earn"), icon: Coins },
    { to: "/roulette", label: t("freeRoulette"), icon: Gift },
    { to: "/games", label: t("games"), icon: Gamepad2 },
    { to: "/promo", label: "ترويج رابط", icon: Megaphone },
  ] : [];

  // Mobile bottom-nav items (top 4 + more)
  const bottomItems = user ? [
    { to: "/dashboard", label: t("dashboard"), icon: LayoutDashboard },
    { to: "/pools", label: t("pools"), icon: Sparkles },
    { to: "/wallet", label: t("wallet"), icon: Wallet },
    { to: "/earn", label: t("earn"), icon: Coins },
  ] : [];

  return (
    <>
      <header className="sticky top-0 z-50 glass-strong">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 h-16 flex items-center justify-between gap-3">
          <Link to={user ? "/dashboard" : "/"} className="flex items-center gap-2" data-testid="logo-link">
            <img src="/assets/sulfa-logo.png" alt="SULFA" className="w-10 h-10 rounded-lg object-contain" />
            <span className="text-base sm:text-xl font-bold text-cyan-gradient">{t("appName")}</span>
          </Link>

          <nav className="hidden lg:flex items-center gap-1">
            {navItems.map((item) => {
              const Icon = item.icon;
              const active = location.pathname.startsWith(item.to);
              return (
                <Link key={item.to} to={item.to} data-testid={`nav-${item.to.slice(1)}-link`}
                  className={`px-2.5 py-2 rounded-lg text-xs flex items-center gap-1.5 ${active ? "bg-[#1A2D4D] text-[#00E5FF]" : "text-[#8FA3BD] hover:text-white hover:bg-[#1A2D4D]/50"}`}>
                  <Icon className="w-3.5 h-3.5" /> {item.label}
                </Link>
              );
            })}
            {user?.role === "admin" && (
              <Link to="/admin" data-testid="nav-admin-link" className="px-2.5 py-2 rounded-lg text-xs flex items-center gap-1.5 text-[#00E676] hover:bg-[#1A2D4D]/50">
                <ShieldCheck className="w-3.5 h-3.5" /> {t("admin")}
              </Link>
            )}
          </nav>

          <div className="flex items-center gap-1.5 sm:gap-2">
            <button onClick={toggleCurrency} data-testid="currency-toggle-btn" className="text-[10px] sm:text-xs px-2 sm:px-2.5 py-1.5 sm:py-2 rounded-lg border border-[#1A2D4D] hover:border-[#FF9500] flex items-center gap-1 text-[#8FA3BD] hover:text-[#FF9500] font-bold">
              <DollarSign className="w-3 h-3 sm:w-3.5 sm:h-3.5" /> {currency}
            </button>
            <button onClick={toggle} data-testid="lang-toggle-btn" className="text-[10px] sm:text-xs px-2 sm:px-2.5 py-1.5 sm:py-2 rounded-lg border border-[#1A2D4D] hover:border-[#00E5FF] flex items-center gap-1 text-[#8FA3BD] hover:text-white">
              <Languages className="w-3 h-3 sm:w-3.5 sm:h-3.5" /> {lang === "ar" ? "EN" : "ع"}
            </button>
            {user && (
              <>
                <Link to="/notifications" className="relative p-1.5 sm:p-2 rounded-lg hover:bg-[#1A2D4D] text-[#8FA3BD] hover:text-white" data-testid="notif-bell-btn">
                  <Bell className="w-4 h-4" />
                  {unread > 0 && (
                    <span className="absolute top-0 end-0 min-w-[14px] h-3.5 px-1 rounded-full bg-[#FF9500] text-[9px] text-[#050E1A] font-bold flex items-center justify-center" data-testid="notif-badge">{unread > 9 ? "9+" : unread}</span>
                  )}
                </Link>
                <button onClick={() => setDrawerOpen(true)} data-testid="open-drawer-btn"
                  className="lg:hidden p-1.5 rounded-lg hover:bg-[#1A2D4D] text-[#8FA3BD]"><Menu className="w-5 h-5" /></button>
                <Link to="/profile" className="hidden lg:inline-flex p-2 rounded-lg hover:bg-[#1A2D4D] text-[#8FA3BD] hover:text-white" data-testid="profile-link"><UserIcon className="w-4 h-4" /></Link>
                <button onClick={handleLogout} data-testid="logout-btn" className="hidden lg:inline-flex p-2 rounded-lg hover:bg-[#1A2D4D] text-[#8FA3BD] hover:text-white">
                  <LogOut className="w-4 h-4" />
                </button>
              </>
            )}
            {!user && (
              <>
                <Link to="/login"><Button variant="ghost" size="sm" data-testid="login-nav-btn">{t("login")}</Button></Link>
                <Link to="/register"><Button size="sm" className="btn-cyan" data-testid="register-nav-btn">{t("register")}</Button></Link>
              </>
            )}
          </div>
        </div>
      </header>

      {/* Mobile slide-in drawer */}
      {drawerOpen && (
        <div className="lg:hidden fixed inset-0 z-[60] flex" onClick={() => setDrawerOpen(false)}>
          <div className="absolute inset-0 bg-black/70" />
          <div className="relative ms-auto w-72 max-w-[85vw] h-full bg-[#0F1F35] border-s border-[#1A2D4D] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
            <div className="p-4 border-b border-[#1A2D4D] flex items-center justify-between">
              <Link to="/profile" className="flex items-center gap-3" onClick={() => setDrawerOpen(false)}>
                <div className="w-10 h-10 rounded-full bg-[#1A2D4D] overflow-hidden flex items-center justify-center">
                  {user?.picture ? <img src={user.picture} alt="" className="w-full h-full object-cover" /> : <UserIcon className="w-5 h-5 text-[#8FA3BD]" />}
                </div>
                <div>
                  <div className="text-sm font-bold">{user?.nickname || user?.name}</div>
                  <div className="text-[10px] text-[#8FA3BD]">{user?.email}</div>
                </div>
              </Link>
              <button onClick={() => setDrawerOpen(false)} className="p-1 text-[#8FA3BD]"><X className="w-5 h-5" /></button>
            </div>
            <nav className="p-2">
              {navItems.map((item) => {
                const Icon = item.icon;
                const active = location.pathname.startsWith(item.to);
                return (
                  <Link key={item.to} to={item.to} data-testid={`drawer-${item.to.slice(1)}-link`}
                    className={`flex items-center gap-3 px-4 py-3 rounded-lg ${active ? "bg-[#1A2D4D] text-[#00E5FF]" : "text-[#8FA3BD] hover:bg-[#1A2D4D]/50"}`}>
                    <Icon className="w-4 h-4" /> {item.label}
                  </Link>
                );
              })}
              {user?.role === "admin" && (
                <Link to="/admin" className="flex items-center gap-3 px-4 py-3 rounded-lg text-[#00E676] hover:bg-[#1A2D4D]/50">
                  <ShieldCheck className="w-4 h-4" /> {t("admin")}
                </Link>
              )}
              <Link to="/profile" className="flex items-center gap-3 px-4 py-3 rounded-lg text-[#8FA3BD] hover:bg-[#1A2D4D]/50">
                <UserIcon className="w-4 h-4" /> {t("profile")}
              </Link>
              <button onClick={handleLogout} className="w-full flex items-center gap-3 px-4 py-3 rounded-lg text-red-400 hover:bg-red-500/10">
                <LogOut className="w-4 h-4" /> {t("logout")}
              </button>
            </nav>
          </div>
        </div>
      )}

      {/* Mobile bottom-nav */}
      {user && (
        <nav className="lg:hidden fixed bottom-0 inset-x-0 z-40 glass-strong border-t border-[#1A2D4D]">
          <div className="grid grid-cols-4 gap-1 px-2 py-2">
            {bottomItems.map((item) => {
              const Icon = item.icon;
              const active = location.pathname.startsWith(item.to);
              return (
                <Link key={item.to} to={item.to} data-testid={`bottom-${item.to.slice(1)}-link`}
                  className={`flex flex-col items-center gap-0.5 py-1.5 rounded-lg text-[10px] ${active ? "text-[#00E5FF]" : "text-[#8FA3BD]"}`}>
                  <Icon className="w-4 h-4" /> {item.label}
                </Link>
              );
            })}
          </div>
        </nav>
      )}
    </>
  );
}
